import type { TPlayer } from 'video.js'
import type { ITextTrackItem } from '../settings'

import { Events } from '|>/shared/events'
import { Intent } from '|>/shared/types/intent'
import { on, register } from '|>/shared/vjs'
import { BaseClickableComponent } from '../base'
import {
  convertTextTrackToItem,
  getAvailableTextTracks,
  saveOffTextTrack,
  saveTextTrack,
  setPreferableTextTrack,
} from '../settings'
import { SubtitlesUnavailableTooltip } from '../subtitles-unavailable-tooltip'

import './closed-captions-button.css'

export const TEXT_TRACK_TURNED_OFF_STORAGE_KEY = 'pureya__text-track-turned-off'

/**
 * Closed Captions toggle button
 */
@register
export class ClosedCaptionsButton extends BaseClickableComponent {
  private textTracksTurnedOff_: boolean = true
  declare unavailableTooltip?: SubtitlesUnavailableTooltip

  constructor(player: TPlayer, options: any) {
    super(player, options)

    // Load initial state from localStorage
    this.loadTextTracksState()

    // Update button state based on current tracks
    this.updateButtonState()
  }

  override handleClick() {
    this.toggleTextTracks()
  }

  /**
   * Load text tracks state from localStorage
   */
  private loadTextTracksState(): void {
    try {
      const stored = localStorage.getItem(TEXT_TRACK_TURNED_OFF_STORAGE_KEY)
      this.textTracksTurnedOff_ = stored ? JSON.parse(stored) : true
    } catch (ignore) {
      this.textTracksTurnedOff_ = true
    }
  }

  /**
   * Save text tracks state to localStorage
   */
  private saveTextTracksState(): void {
    try {
      localStorage.setItem(
        TEXT_TRACK_TURNED_OFF_STORAGE_KEY,
        JSON.stringify(this.textTracksTurnedOff_)
      )
    } catch (ignore) {
      // localStorage might not be available
    }
  }

  /**
   * Toggle text tracks on/off
   * Only changes pureya__text-track-turned-off flag, does not modify pureya__text-track
   */
  private toggleTextTracks(): void {
    this.textTracksTurnedOff_ = !this.textTracksTurnedOff_
    this.saveTextTracksState()

    if (this.textTracksTurnedOff_) {
      // Turn off all subtitle tracks
      // Do NOT save anything to pureya__text-track - keep existing selection
      this.player_.trigger(Events.User.Intent, Intent.DisableSubtitles)
      this.disableAllSubtitleTracks()
    } else {
      // Turn on the preferable subtitle track
      // Do NOT save to pureya__text-track - only manual selection should save
      this.player_.trigger(Events.User.Intent, Intent.EnableSubtitles)
      this.enablePreferableSubtitleTrack()
    }

    this.updateButtonState()

    // Trigger event for other components
    this.player_.trigger(Events.Media.SubtitleTracksChanged)
  }

  /**
   * Disable all subtitle and caption tracks
   */
  private disableAllSubtitleTracks(): void {
    const tracks = this.player_.textTracks()
    if (!tracks) return

    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i]
      if (track.kind === 'captions' || track.kind === 'subtitles') {
        track.mode = 'disabled'
      }
    }
  }

  /**
   * Enable preferable subtitle track based on stored preferences and app locale
   * Does NOT save to localStorage - saving only happens on manual selection
   */
  private enablePreferableSubtitleTrack(): void {
    const availableTracks = getAvailableTextTracks(this.player_)
    if (availableTracks.length === 0) return

    // Get app locale from player options
    const appLocale = this.player_.language()

    setPreferableTextTrack({
      textTracks: availableTracks,
      appLocale,
      onTrackSelected: (selectedTrack: ITextTrackItem) => {
        const tracks = this.player_.textTracks()
        if (!tracks) return

        for (let i = 0; i < tracks.length; i++) {
          const track = tracks[i]
          if (
            (track.kind === 'captions' || track.kind === 'subtitles') &&
            track.language === selectedTrack.language &&
            track.kind === selectedTrack.kind
          ) {
            track.mode = 'showing'
          } else {
            track.mode = 'disabled'
          }
        }
      },
    })
  }

  /**
   * Update button visual state based on current tracks availability and state
   */
  private updateButtonState(): void {
    const hasAvailableTracks = this.hasAvailableSubtitleTracks()
    const isEnabled = !this.textTracksTurnedOff_ && hasAvailableTracks

    // Update button appearance
    this.toggleClass('vjs-x-closed-captions-enabled', isEnabled)
    this.toggleClass('vjs-x-closed-captions-disabled', !hasAvailableTracks)

    // Update tooltip visibility
    this.updateTooltip()
  }

  /**
   * Check if there are any available subtitle tracks
   */
  private hasAvailableSubtitleTracks(): boolean {
    const tracks = this.player_.textTracks()
    if (!tracks || tracks.length === 0) return false

    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i]
      if (track.kind === 'captions' || track.kind === 'subtitles') {
        return true
      }
    }
    return false
  }

  /**
   * Sync button state with manual track selection from menu
   * Called when user selects a track manually in the menu
   */
  private syncWithManualSelection(
    data: Events.TextTrack.ManualSelection
  ): void {
    // console.log('syncWithManualSelection', data)
    const { isOff } = data

    if (isOff) {
      // User selected "Off"
      this.textTracksTurnedOff_ = true
      this.saveTextTracksState()
      saveOffTextTrack()
      this.updateButtonState()
    } else {
      // User selected a specific track
      this.textTracksTurnedOff_ = false
      this.saveTextTracksState()

      // Find and save the selected track
      const tracks = this.player_.textTracks()
      if (tracks) {
        for (let i = 0; i < tracks.length; i++) {
          const track = tracks[i]
          if (
            (track.kind === 'captions' || track.kind === 'subtitles') &&
            track.mode === 'showing'
          ) {
            const trackItem = convertTextTrackToItem(track)
            saveTextTrack(trackItem)
            break
          }
        }
      }

      this.updateButtonState()
    }
  }

  /**
   * Get current text tracks state
   */
  getTextTracksTurnedOff(): boolean {
    return this.textTracksTurnedOff_
  }

  // Listen to track changes to update button state
  @on(Events.Media.SubtitleTracksChanged)
  onSubtitleTracksChanged() {
    this.updateButtonState()
  }

  /**
   * Apply stored textTracksTurnedOff state to actual tracks
   * This ensures that if user had tracks turned off, they stay off on load
   * If tracks should be on, applies the stored track selection from localStorage
   */
  private applyStoredStateToTracks(): void {
    if (this.textTracksTurnedOff_) {
      this.disableAllSubtitleTracks()
    } else {
      this.enablePreferableSubtitleTrack()
    }
  }

  // Listen to custom event for manual track selection from menu
  @on(Events.TextTrack.ManualSelection)
  onManualTrackSelection(data: Events.TextTrack.ManualSelection) {
    this.syncWithManualSelection(data)
  }

  @on('loadeddata')
  onLoadedData() {
    this.requestAnimationFrame(() => {
      this.applyStoredStateToTracks()
    })
  }

  /**
   * Update tooltip visibility based on button state
   */
  private updateTooltip(): void {
    if (!this.unavailableTooltip) return

    const hasAvailableTracks = this.hasAvailableSubtitleTracks()

    if (hasAvailableTracks) {
      this.unavailableTooltip.hide()
    } else {
      this.unavailableTooltip.show()
    }
  }
}

ClosedCaptionsButton.options = {
  className: 'vjs-x-closed-captions-button',
  children: [SubtitlesUnavailableTooltip.as('unavailableTooltip')],
}
